<template>
  <teleport to="body">
    <div v-if="props.isOpen" class="frisbuy-mobile-review-sheet">
      <div class="frisbuy-mobile-review-sheet__header">
        <button
          class="frisbuy-mobile-review-sheet__back"
          type="button"
          @click="emit('close')"
        >
          <svg
            width="40"
            height="40"
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="40" height="40" fill="white" />
            <path
              d="M23.3722 26.0335C23.7266 25.6504 23.7269 25.0593 23.3729 24.6759L19.0553 20L23.3729 15.3241C23.7269 14.9407 23.7266 14.3496 23.3722 13.9665L23.2117 13.7931C22.8159 13.3654 22.1396 13.3654 21.7438 13.7931L16.6285 19.3208C16.2738 19.7041 16.2738 20.2959 16.6285 20.6792L21.7438 26.2069C22.1396 26.6346 22.8159 26.6346 23.2117 26.2069L23.3722 26.0335Z"
              fill="#999999"
            />
          </svg>
        </button>

        <span>{{ reviewTitle }}</span>
      </div>

      <div class="frisbuy-mobile-review-sheet__body">
        <div class="frisbuy-mobile-review-sheet__author-wrap">
          <ReviewAuthor
            :customerName="
              props.post.instagramPost.authorUser.fio ||
              `@${props.post.instagramPost.authorUser.username}`
            "
            :avatarColorIndex="props.reviewIndex"
            :reviewDate="props.reviewDate"
            :rating="props.post.frisbuyPost.rating"
            :isSoftStarIcon="false"
            view="popup"
          />
        </div>

        <div class="frisbuy-mobile-review-sheet__text">
          <div v-if="props.post.frisbuyPost.positiveComment">
            <span class="frisbuy-mobile-review-sheet__text-prefix"
              >Преимущества:
            </span>
            <span>{{ props.post.frisbuyPost.positiveComment }}</span>
          </div>

          <div v-if="props.post.frisbuyPost.negativeComment">
            <span class="frisbuy-mobile-review-sheet__text-prefix"
              >Недостатки:
            </span>
            <span>{{ props.post.frisbuyPost.negativeComment }}</span>
          </div>

          <div v-if="props.post.frisbuyPost.text">
            <span>{{ props.post.frisbuyPost.text }}</span>
          </div>
        </div>

        <PopupReviewMediaList
          :mediaList="currentPostMediaList"
          :selectedMediaThumbnailUrl="props.selectedMediaThumbnailUrl"
          playIconVariant="mobileSheet"
          @selectMedia="onSelectMedia"
        />
      </div>

      <div
        class="frisbuy-mobile-review-sheet__safe-area"
        aria-hidden="true"
      ></div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { computed } from "vue";
import ReviewAuthor from "./ReviewAuthor.vue";
import PopupReviewMediaList from "./PopupReviewMediaList.vue";
import { imageWithPostId, IMarketplaceReviewsPost } from "../interfaces";

const props = defineProps<{
  isOpen: boolean;
  post: IMarketplaceReviewsPost;
  bestPostIds?: number[];
  reviewIndex?: number;
  reviewDate: string;
  selectedMediaThumbnailUrl?: string;
  mediaList?: Array<imageWithPostId>;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "selectMedia", media: imageWithPostId): void;
}>();

const currentPostMediaList = computed<imageWithPostId[]>(() => {
  const postId =
    props.post.instagramPost?.frisbuyId || props.post.providerPost?.frisbuyId;

  return props.post.media.map((media) => ({
    ...media,
    postId,
  }));
});

const reviewTitle = computed(() => {
  const postId =
    props.post.instagramPost?.frisbuyId || props.post.providerPost?.frisbuyId;
  const isBestPost = Boolean(
    typeof postId === "number" && props.bestPostIds?.includes(postId)
  );

  return isBestPost ? "Лучший отзыв" : "Отзыв покупателя";
});

function onSelectMedia(media: imageWithPostId) {
  emit("selectMedia", media);
  emit("close");
}
</script>

<style scoped lang="scss">
.frisbuy-mobile-review-sheet {
  position: fixed;
  inset: 0;
  width: 100dvw;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  z-index: 9999;
  background: #ffffff;
  color: #303133;
  display: grid;
  grid-template-rows: 40px minmax(0, 1fr) 40px;
  overflow: hidden;
}

.frisbuy-mobile-review-sheet__header {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 40px;
  box-sizing: border-box;

  span {
    font-size: 16px;
    line-height: 24px;
    font-weight: 700;
    color: rgba(153, 153, 153, 1);
  }
}

.frisbuy-mobile-review-sheet__back {
  position: absolute;
  left: 0;
  top: 0;
  width: 40px;
  height: 40px;
  border: 0;
  padding: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.frisbuy-mobile-review-sheet__body {
  padding: 0 16px 0px;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.frisbuy-mobile-review-sheet__body > * {
  flex-shrink: 0;
}

.frisbuy-mobile-review-sheet__safe-area {
  height: 40px;
}

.frisbuy-mobile-review-sheet__author-wrap {
  padding: 20px 0 16px;
}

.frisbuy-mobile-review-sheet__text {
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #212121;
}

.frisbuy-mobile-review-sheet__text-prefix {
  font-weight: 700;
}

.frisbuy-mobile-review-sheet
  :deep(.frisbuy-review-author--popup .frisbuy-review-author__avatar) {
  display: flex;
  width: 48px;
  min-width: 48px;
  height: 48px;
  font-size: 28px;
  line-height: 28px;
  font-weight: 400;
  color: #ffffff;
}

.frisbuy-mobile-review-sheet
  :deep(.frisbuy-review-author--popup .frisbuy-review-author__meta) {
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.frisbuy-mobile-review-sheet :deep(.frisbuy-review-author--popup) {
  align-items: flex-start;
}

.frisbuy-mobile-review-sheet
  :deep(.frisbuy-review-author--popup .frisbuy-review-author__date) {
  display: inline-block;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  color: #999999;
}

.frisbuy-mobile-review-sheet
  :deep(.frisbuy-review-author--popup .frisbuy-review-author__name) {
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  color: #212121;
}

.frisbuy-mobile-review-sheet
  :deep(.frisbuy-review-author__rating-mobile-stars) {
  display: none;
}

.frisbuy-mobile-review-sheet :deep(.frisbuy-review-author__rating-simple) {
  display: inline-flex;
  align-items: center;
  color: #212121;
  gap: 2px;
  margin-top: 4px;
}

@media (max-width: 1024px) {
  .frisbuy-mobile-review-sheet
    :deep(
      .frisbuy-review-author--popup .frisbuy-review-author__rating-mobile-stars
    ) {
    display: none;
  }

  .frisbuy-mobile-review-sheet
    :deep(.frisbuy-review-author--popup .frisbuy-review-author__rating-simple) {
    display: inline-flex;
  }
}

.frisbuy-mobile-review-sheet :deep(.frisbuy-review-author__rating-simple span) {
  display: inline-block;
  vertical-align: middle;
  font-size: 18px;
  line-height: 20px;
  font-weight: 400;
}

.frisbuy-mobile-review-sheet :deep(.frisbuy-review-author__rating-simple svg) {
  display: inline-block;
  vertical-align: middle;
  width: 24px;
  height: 24px;
}

.frisbuy-mobile-review-sheet
  :deep(.frisbuy-review-author__rating-simple svg path) {
  fill: #212121;
}

.frisbuy-mobile-review-sheet :deep(.frisbuy-modal-media-list) {
  margin-top: 8px;
  display: grid;
  grid-template-columns: repeat(auto-fill, 72px);
  justify-content: flex-start;
  gap: 4px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  padding: 0;
}

.frisbuy-mobile-review-sheet :deep(.frisbuy-modal-media-list__item) {
  width: 72px;
  height: 72px;
  border-radius: 8px;
}

.frisbuy-mobile-review-sheet :deep(.frisbuy-modal-media-list__image) {
  width: 72px;
  height: 72px;
  border-radius: 8px;
}

.frisbuy-mobile-review-sheet
  :deep(.frisbuy-modal-media-list__item--selected::after) {
  display: none;
}

.frisbuy-mobile-review-sheet :deep(.frisbuy-modal-media-list__play-icon) {
  width: 48px;
  height: 48px;
}

@media (min-width: 1025px) {
  .frisbuy-mobile-review-sheet {
    display: none;
  }
}
</style>
